import { Dispatch } from "react-redux";
import { AuthActionCreators, IApplicationState } from "../";
import { ActionTypes } from "./action-types";
import { WageAndTravelReimbursementRequestStatus } from "../../enums/watr-reimbursement-request-status";
import { AdditionalWeeksReason, DashDonationType } from "../../enums/index";
import { getRequiredDocumentsForCenterApproval } from "../../services/additional-week-policy";
import { IActionCenterToDo, IBaseResponse, IListItem, IActionCenterOther, IPendingSwap, IActionCenterCompliance, IImmunofreeCandidate, IWageAndTravelRequestWeek, IPhysicianLetterWeekUpload, IBloodDrawConfirmationContract } from "../../models/index";
import { ICenterPerformance, ICenterPerformanceCompliance, ICenterPerformanceImmediateAttention, IRelativePerformance, IScoreCard } from '../../models/center-performance';
import { IRpChainEndDeclineRatio } from '../../models/center-performance/relative-performance';
import { IGet, IPost } from "../authorization";
import { IPut } from "../authorization/actions";
import { IPendingSwaps } from "./state";
import { AxiosError } from "axios";
import { ICoordinatorListByCenter } from "@nkr/models/coordinator-list-by-center";

export interface IActionCenterLookupData {
    referralDispositions: IListItem[],
    coordinatorList: ICoordinatorListByCenter[],
    donorRemoteStatus: IListItem[]
}

export interface IActionCenterToDoResponse {
    data: {
        success: boolean,
        message: string,
        results: IActionCenterToDo
    }
}

export interface IActionCenterOtherResponse {
    data: {
        success: boolean,
        message: string,
        results: IActionCenterOther
    }
}

export interface IActionCenterComplianceResponse {
    data: {
        success: boolean,
        message: string,
        results: IActionCenterCompliance
    }
}

export interface IActionCenterPendingSwapsResponse {
    data: {
        success: boolean,
        message: string,
        results: IPendingSwaps
    }
}

export interface IActionCenterLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IActionCenterLookupData
    }
}

export interface IActionCenterPerformanceResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            data: ICenterPerformance,
            runTimeSeconds: number
        }
    }
}

export interface IActionCenterCenterPerformanceImmediateAttentionAndComplianceResponse {
    data: {
        success: boolean,
        results: {
            immediateAttention: ICenterPerformanceImmediateAttention,
            compliance: ICenterPerformanceCompliance
            runTimeSeconds: number
        }
    }
}

export interface IRmcrResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            data: IScoreCard[],
            runTimeSeconds: number
        }
    }
}

export interface IRelativePerformanceResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            relativePerformance: IRelativePerformance,
            memberCenterCount: number,
            runTimeSeconds: number
        }
    }
}

export const ActionCreators = {
    getTodoItems: (centerId: number, liverOnly: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterToDoResponse> => {
        let config: IGet = {
            url: __API__ + "/center/" + centerId + "/to-do"
        };

        if (liverOnly != null) {
            config.url += `?liverOnly=${liverOnly}`;
        }

        return dispatch(AuthActionCreators.apiGet(config))
            .then((response: IActionCenterToDoResponse) => {
                dispatch(ActionCreators.setTodoItems(response.data.results));
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setTodoItems: (todo: IActionCenterToDo) => {
        return {
            type: ActionTypes.SET_TODO_ITEMS,
            todo
        };
    },
    getOtherItems: (centerId: number, liverOnly: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterOtherResponse> => {
        let config: IGet = {
            url: __API__ + "/center/" + centerId + "/other"
        };

        if (liverOnly != null) {
            config.url += `?liverOnly=${liverOnly}`;
        }

        return dispatch(AuthActionCreators.apiGet(config)).then((response: IActionCenterOtherResponse) => {
            dispatch(ActionCreators.setOtherItems(response.data.results));
            return Promise.resolve(response);
        });
    },
    setOtherItems: (other: IActionCenterOther) => {
        return {
            type: ActionTypes.SET_OTHER_ITEMS,
            other
        };
    },
    getComplianceItems: (centerId: number, liverOnly: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterComplianceResponse> => {
        let config: IGet = {
            url: __API__ + "/center/" + centerId + "/compliance"
        };

        if (liverOnly != null) {
            config.url += `?liverOnly=${liverOnly}`;
        }

        return dispatch(AuthActionCreators.apiGet(config)).then((response: IActionCenterComplianceResponse) => {
            dispatch(ActionCreators.setComplianceItems(response.data.results));
            return Promise.resolve(response);
        });
    },
    setComplianceItems: (compliance: IActionCenterCompliance) => {
        return {
            type: ActionTypes.SET_COMPLIANCE_ITEMS,
            compliance
        };
    },
    getPendingSwaps: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterPendingSwapsResponse> => {
        let config: IGet = {
            url: __API__ + "/center/" + centerId + "/pending-swaps"
        };

        return dispatch(AuthActionCreators.apiGet(config)).then((response: IActionCenterPendingSwapsResponse) => {
            dispatch(ActionCreators.setPendingSwaps(response.data.results));
            return Promise.resolve(response);
        });
    },
    setPendingSwaps: (data: IPendingSwaps) => {
        return {
            type: ActionTypes.SET_PENDING_SWAPS,
            data
        };
    },
    setDonorSwaps: (swaps: IPendingSwap[]) => {
        return {
            type: ActionTypes.SET_PENDING_SWAPS_DONOR,
            swaps
        };
    },
    getPastTransplants: (centerId: number, startDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterPendingSwapsResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/past-transplants/${startDate}`
        };

        return dispatch(AuthActionCreators.apiGet(config)).then((response: IActionCenterPendingSwapsResponse) => {
            dispatch(ActionCreators.setPastTransplants(response.data.results));
            return Promise.resolve(response);
        });
    },
    setPastTransplants: (data: IPendingSwaps) => {
        return {
            type: ActionTypes.SET_PAST_TRANSPLANTS,
            data
        };
    },
    getLookupData: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterLookupDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/action-center-lookup-data`
        };

        return dispatch(AuthActionCreators.apiGet(config)).then((response: IActionCenterLookupDataResponse) => {
            dispatch(ActionCreators.setLookupData(response.data.results));
            return Promise.resolve(response);
        });
    },
    setLookupData: (data: IActionCenterLookupData) => {
        return {
            type: ActionTypes.SET_LOOKUP_DATA,
            data
        };
    },
    setChainEndDeclineRatio: (chainEndDeclineRatio: IRpChainEndDeclineRatio) => {
        return {
            type: ActionTypes.SET_CHAIN_END_DECLINE_RATIO,
            chainEndDeclineRatio
        };
    },
    getFinalXmResults: (recipientId: number, donorId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + '/swap/get-final-xm/' + donorId + "/" + recipientId,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    updateLostChainStarters: (updates: any[], deletes: number[]) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/donor/lost-chain-starters`,
            data: {
                updates,
                deletes
            }
        };

        return dispatch(AuthActionCreators.apiPost(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    confirmDashDonations: (list: { id: number, surgeryCompletedDateTime: string }[]) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/donor-intake/confirm-donations`,
            data: list
        };

        return dispatch(AuthActionCreators.apiPost(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    confirmBloodDraw: (contract: IBloodDrawConfirmationContract) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPut = {
            url: `${__API__}/center/confirm-blood-draw`,
            data: contract
        };

        return dispatch(AuthActionCreators.apiPut(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    confirmBloodDrawCompletion: (contract: IBloodDrawConfirmationContract) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPut = {
            url: `${__API__}/center/confirm-blood-draw-completion`,
            data: contract
        };

        return dispatch(AuthActionCreators.apiPut(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    requestFinalXm: (donorId: number, recipientId: number, bloodDrawDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterPerformanceResponse> => {
        let options: IGet = {
            url: `${__API__}/orders/final-xm/donor/${donorId}/recipient/${recipientId}?bloodDrawDate=${bloodDrawDate}`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    requestSerology: (donorId: number, recipientId: number, bloodDrawDate?: string) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterPerformanceResponse> => {
        let url = `${__API__}/orders/serology/donor/${donorId}/recipient/${recipientId}`;

        if (bloodDrawDate) {
            url += `?bloodDrawDate=${bloodDrawDate}`;
        }

        let options: IGet = {
            url: url,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    inviteImmunoFreeCandidate: (centerId: number, candidate: IImmunofreeCandidate) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let config: IPost = {
            url: `${__API__}/immuno-free/center/${centerId}/invite`,
            data: candidate
        };

        return dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },

    uploadFinalXm: (donorId: number, recipientId: number, acceptable: number, rootCause: string, correctiveAction: string, initials: string, finalXmFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('acceptable', acceptable.toString());
        formData.append('rootCause', rootCause);
        formData.append('correctiveAction', correctiveAction);
        formData.append('initials', initials);
        formData.append('finalXmFile', finalXmFile);

        var config: IPost = {
            url: `${__API__}/swap/upload-final-xm/${donorId}/${recipientId}`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },
    uploadSerology: (donorId: number, recipientId: number, centerId: number, serologyFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('serologyFile', serologyFile);

        var config: IPost = {
            url: `${__API__}/swap/center/${centerId}/upload-serology/${donorId}/${recipientId}`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },

    getCenterPerformance: (centerId: number, startDate: string, endDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterPerformanceResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/performance?startDate=${startDate}&endDate=${endDate}`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getImmediateAttentionAndCompliance: (centerId: number, startDate: string, endDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IActionCenterCenterPerformanceImmediateAttentionAndComplianceResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/immediate-attention-compliance?startDate=${startDate}&endDate=${endDate}`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getRmcr: (centerId: number, startDate: string, endDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IRmcrResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/rmcr?startDate=${startDate}&endDate=${endDate}`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    getRelativePerformance: (centerId: number, startDate: string, endDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IRelativePerformanceResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/relative-performance?startDate=${startDate}&endDate=${endDate}`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    updateWeekReimbursementRequestStatus: (centerId: number, list: IWageAndTravelRequestWeek[], physicianLetters?: IPhysicianLetterWeekUpload[]) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        const url = `${__API__}/center/watr/request/${centerId}/week-reimbursement-request-status-change`;

        const needsMultipart = list.some((w) => w.status === WageAndTravelReimbursementRequestStatus.CenterApproved);

        if (needsMultipart) {
            let formData = new FormData();
            formData.append('changes', JSON.stringify(list));
            const attestationSent = new Set<string>();
            (physicianLetters || []).forEach((p) => {
                const key = `complicationsReport_${p.requestId}_${p.week}`;
                formData.append(key, p.file);
            });

            list.forEach((w) => {
                if (w.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
                    return;
                }
                if (w.additionalWeeksReason !== AdditionalWeeksReason.Complications) {
                    return;
                }
                if (w.policyOrganType == null) {
                    return;
                }
                const docs = getRequiredDocumentsForCenterApproval(w.policyOrganType as DashDonationType, w.week, w.additionalWeeksReason);
                if (docs.length === 0) {
                    return;
                }
                const attestKey = `${w.requestId}_${w.week}`;
                if (!attestationSent.has(attestKey)) {
                    attestationSent.add(attestKey);
                    formData.append(`attestation_${w.requestId}_${w.week}`, 'true');
                }
            });

            return dispatch(AuthActionCreators.apiPostFormData({ url, data: formData }));
        }

        return dispatch(AuthActionCreators.apiPost({ url, data: list }));
    }
};
